/** @format */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * A single actor role as held by the role registry.
 * `id` is the value persisted in a stage's `actor_roles` array (the role name).
 */
export interface RegistryRole {
  id: string;
  /** Numeric primary key — needed to persist role → user assignments. */
  roleId: number;
  label: string;
  description: string;
  active: boolean;
}

interface ActorRoleDto {
  workflowActorRoleId: number;
  name: string;
  description: string | null;
  isActive: boolean;
}

/**
 * Role registry for workflow stage actors (US-05).
 *
 * Backed by the API (`/workflows/roles`) with an in-memory cache so consumers
 * can read roles synchronously (the builder evaluates role state inside change
 * detection). `load()` refreshes the cache; mutations go to the API and update
 * the cache on success.
 */
@Injectable({ providedIn: 'root' })
export class RoleRegistryService {
  private baseUrl = `${environment.apiUrl}/workflows/roles`;
  private registry: RegistryRole[] = [];
  private idByName = new Map<string, number>();
  private loaded = false;

  constructor(private http: HttpClient) {}

  /** Fetch the registry from the API into the cache. */
  load(): Observable<RegistryRole[]> {
    return this.http.get<ActorRoleDto[]>(this.baseUrl).pipe(
      tap(() => this.idByName.clear()),
      map((dtos) => dtos.map((d) => this.toRole(d))),
      tap((roles) => {
        this.registry = roles;
        this.loaded = true;
      })
    );
  }

  /** Whether the registry has been fetched at least once. */
  isLoaded(): boolean {
    return this.loaded;
  }

  /** All roles, active and inactive (for the management modal). */
  getAllRoles(): RegistryRole[] {
    return this.registry.map((r) => ({ ...r }));
  }

  /** Active role ids — the only roles offered when assigning actors (US-05 AC-01). */
  getActiveRoles(): string[] {
    return this.registry.filter((r) => r.active).map((r) => r.id);
  }

  /** Whether a role id is currently active (US-05 AC-04 referential check). */
  isActive(roleId: string): boolean {
    const role = this.registry.find((r) => r.id === roleId);
    return !!role && role.active;
  }

  /** Add a new role via the API; updates the cache on success. */
  addRole(name: string, description = ''): Observable<RegistryRole> {
    return this.http
      .post<ActorRoleDto>(this.baseUrl, { name: name.trim(), description: description.trim() })
      .pipe(
        map((d) => this.toRole(d)),
        tap((role) => this.registry.push(role))
      );
  }

  /** Update a role's name, description and status via the API; updates the cache on success. */
  updateRole(
    id: number,
    name: string,
    description: string,
    isActive: boolean
  ): Observable<RegistryRole> {
    const prevName = this.nameById(id);
    return this.http
      .put<ActorRoleDto>(`${this.baseUrl}/${id}`, {
        name: name.trim(),
        description: description.trim(),
        isActive,
      })
      .pipe(
        map((d) => this.toRole(d)),
        tap((role) => {
          const idx = this.registry.findIndex((r) => r.id === prevName);
          if (idx >= 0) this.registry[idx] = role;
        })
      );
  }

  private nameById(id: number): string | undefined {
    for (const [name, rid] of this.idByName.entries()) if (rid === id) return name;
    return undefined;
  }

  /** Activate / deactivate a role via the API; updates the cache on success. */
  setActive(roleId: string, active: boolean): Observable<RegistryRole | null> {
    const id = this.idByName.get(roleId);
    if (id == null) return of(null);
    return this.http
      .patch<ActorRoleDto>(`${this.baseUrl}/${id}/active`, { isActive: active })
      .pipe(
        map((d) => this.toRole(d)),
        tap((role) => {
          const idx = this.registry.findIndex((r) => r.id === roleId);
          if (idx >= 0) this.registry[idx] = role;
        })
      );
  }

  private toRole(d: ActorRoleDto): RegistryRole {
    this.idByName.set(d.name, d.workflowActorRoleId);
    return {
      id: d.name,
      roleId: d.workflowActorRoleId,
      label: d.name,
      description: d.description ?? '',
      active: d.isActive,
    };
  }
}
